/**********************************************************************************************************
 *   COMMON FUNCTIONS
 **********************************************************************************************************/
import { Navigate, useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'

import globalCookies from 'helpers/cookies'
import { INVOICE_STATUS, SERVICE_STATUS } from 'helpers/constants'
import { store } from 'store/store'
import { Address, Account, CurrentAccount } from 'models/account'
import { toast } from 'react-hot-toast'

const select = (state: object = {}): object => {
    return state
}

const uid = (): string => {
    return (Math.random().toString(36) + Date.now().toString(36)).substr(2, 10)
}

/*   VALIDATION
**********************************************************************************************************/
function PrivateRoute({ children }: { children: JSX.Element }): JSX.Element {
    const authStatus = select(store.getState()) // TODO: add to .app.appAuthorisationStatus
    const location = useLocation()

    if (!authStatus) {
        return <Navigate to="/login" state={{ from: location }} />
    }

    return children
}

/*   VALIDATION
**********************************************************************************************************/
const regex = {
    password: /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
    phone: /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/,
    internationalPhone: /^(?:\+|\.| |[0-9])*$/,
    domain: /^[a-z0-9]+(-[a-z0-9]+)*$/i,
    domainWithExtension: /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/,
    dateOfBirth: /^(0[1-9]|[12][0-9]|3[01])[- /.](0[1-9]|1[012])[- /.](19|20)\d\d$/
}

/*   ACCOUNT
**********************************************************************************************************/
function mapToAccount(account: CurrentAccount): Account {
    return {
        ...account.account,
        is_primary: account.is_primary,
        expiry: account.expiry,
        role: account.role
    }
}
/*   DOMAIN CONTACT
**********************************************************************************************************/
const DomainContact: Address = {
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    organisation: '',
    address1: '',
    address2: '',
    suburb: '',
    postcode: '',
    state: 'VIC',
    country: 'AU'
}

/*   DATA FUNCTIONS
**********************************************************************************************************/
function getInvoiceStatusColour(status: string = ''): string {
    switch (status) {
        case INVOICE_STATUS.PAID:
            return 'confirm'
        case INVOICE_STATUS.UNPAID:
            return 'alternative'
        case INVOICE_STATUS.DISPUTED:
        case INVOICE_STATUS.CANCELED:
        case INVOICE_STATUS.FRAUD:
            return 'attention'
        case INVOICE_STATUS.REFUNDED:
        case INVOICE_STATUS.DRAFT:
        default:
            return 'secondary'
    }
}

function getServiceStatusColour(status: string = ''): string {
    switch (status) {
        case SERVICE_STATUS.ACTIVE:
            return 'confirm'
        case SERVICE_STATUS.PENDING:
            return 'alternative'
        case SERVICE_STATUS.TERMINATED:
            return 'attention'
        case SERVICE_STATUS.SUSPENDED:
        case SERVICE_STATUS.CANCELLED:
        default:
            return 'secondary'
    }
}

function getStatesByCountryCode(countryCode: string = 'AU'): { state_code: string, state: string }[] {
    if (countryCode === 'AU') {
        return [
            { state_code: 'ACT', state: 'Australian Capital Territory' },
            { state_code: 'NSW', state: 'New South Wales' },
            { state_code: 'QLD', state: 'Queensland' },
            { state_code: 'VIC', state: 'Victoria' },
            { state_code: 'SA', state: 'South Australia' },
            { state_code: 'WA', state: 'Western Australia' },
            { state_code: 'TAS', state: 'Tasmania' }
        ]
    }

    if (countryCode === 'NZ') {
        return [
            { state_code: 'NTL', state: 'Northland' },
            { state_code: 'AUK', state: 'Auckland' },
            { state_code: 'WKO', state: 'Waikato' },
            { state_code: 'BOP', state: 'Bay of Plenty' },
            { state_code: 'GIS', state: 'Gisborne' },
            { state_code: 'HKB', state: 'Hawke\'s Bay' },
            { state_code: 'TKI', state: 'Taranaki' },
            { state_code: 'MWT', state: 'Manawatu-Whanganui' },
            { state_code: 'WGN', state: 'Wellington' },
            { state_code: 'TAS', state: 'Tasman' },
            { state_code: 'NSN', state: 'Nelson' },
            { state_code: 'MBH', state: 'Marlborough' },
            { state_code: 'WTC', state: 'West Coast' },
            { state_code: 'CAN', state: 'Canterbury' },
            { state_code: 'OTA', state: 'Otago' },
            { state_code: 'STL', state: 'Southland' }
        ]
    }

    return []
}

function getBillingCycle(name: string = ''): string {
    switch (name) {
        case 'Monthly':
            return '/mo'
        case 'Quarterly':
            return '/3mo'
        case 'Semi-Annually':
            return '/6mo'
        case 'Annually':
            return '/yr'
        case 'Biennially':
            return '/2yr'
        case 'Triennially':
            return '/3yr'
        case 'One Time':
            return ''
        case '1 Year':
            return '/1yr'
        case '2 Years':
            return '/2yr'
        case '3 Years':
            return '/3yr'
        case '4 Years':
            return '/4yr'
        case '5 Years':
            return '/5yr'
        case '6 Years':
            return '/6yr'
        case '7 Years':
            return '/7yr'
        case '8 Years':
            return '/8yr'
        case '9 Years':
            return '/9yr'
        case '10 Years':
            return '/10yr'
        default:
            return '/mo'
    }
}

/*   STRING FUNCTIONS
**********************************************************************************************************/
function getInitials(fullName: string = ''): string {
    const nameArr = fullName.split('')
    const initials = nameArr.filter(char => /[A-Z]/.test(char))

    return initials.join('')
}

function removePropertyByKeys(object: object, keys: string[]): object {
    return Object.fromEntries(Object.entries(object!).filter(([key]) => !keys.includes(key)))
}

function titleCase(str: string = ''): string {
    return str.replace(/[^a-zA-Z]/g, ' ').replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
}

function camelCase(string: string = ''): string {
    return string.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
}

function snakeCase(string: string = ''): string {
    return string && string!.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)!
        .map(x => x.toLowerCase())
        .join('_')
}

function getDomainName(domainName: string = ''): string {
    const values = domainName.replace('www.', '').toLowerCase().split('.')

    return values[0]
}

function formatDescription(description: string = ''): string {
    let desc: string

    if (/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/ig.test(description.toLowerCase())) {
        desc = description.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/ig, ' <span style="color: #6B7280">$&</span>')
    } else {
        if (/[^!@][a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9-]{2,})+/g.test(description)) {
            desc = description.replace(/[^!@][a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9-]{2,})+/g, '<span style="color: #6B7280">$&</span>')
        } else {
            desc = description
        }
    }

    return desc
}

function extractNumberFromString(string: string = ''): number {
    return parseInt(string.replace(/^\D+/g, ''), 10)
}

async function copyToClipboard(text: string = '') {
    try {
        await navigator.clipboard.writeText(text)
        const success = {
            detail: 'Copied Successfully',
            type: 'SUCCESS'
        }
        // @ts-ignore
        toast.custom(success, {
            duration: 5000
        })
    } catch (e) {
        const error = {
            detail: 'Failed to copy. Please check browser permissions',
            type: 'ERROR'
        }
        // @ts-ignore
        toast.custom(error, {
            duration: 5000
        })
    }
}

function parseISOToLocalString(date: string) {
    return parseISO(date).setLocale('en-AU').toLocaleString()
}

/*   OBJECT FUNCTIONS
**********************************************************************************************************/
function isObjectEmpty(object: object = {}): boolean {
    return Object.keys(object).length === 0
}

function flattenObject(object: object = {}): object {
    const flattened = {}

    Object.keys(object).forEach((key: string) => {
        if (typeof object[key as keyof typeof object] === 'object' && object[key as keyof typeof object] !== null) {
            Object.assign(flattened, flattenObject(object[key as keyof typeof object]))
        } else {
            flattened[key as keyof typeof flattened] = object[key as keyof typeof object]
        }
    })

    return flattened
}

function renameKeys(keysMap: object = {}, object: object = {}): object {
    return Object.keys(object).reduce((acc, key) => {
        const renameObject = {
            [keysMap[key as keyof typeof keysMap] || key]: object[key as keyof typeof object]
        }

        return {
            ...acc,
            ...renameObject
        }
    }, {})
}

function removeUndefinedNullEmptyStringFromObject(object: object): object {
    return Object.keys(object).reduce((acc, key) => {
        if (object[key as keyof typeof object] !== null && object[key as keyof typeof object] !== undefined && object[key as keyof typeof object] !== '') {
            acc[key as keyof typeof acc] = object[key as keyof typeof object]
        }

        return acc
    }, {})
}

function replaceNullToEmptyString(object: object = {}): object {
    return Object.keys(object).reduce((acc, key) => {
        const replaceObject = {
            [key]: object[key as keyof typeof object] === null || object[key as keyof typeof object] === undefined ? '' : object[key as keyof typeof object]
        }

        return {
            ...acc,
            ...replaceObject
        }
    }, {})
}

function downloadFile(endpoint = '', fileName = ''): Promise<void> {
    const url = `${process.env.REACT_APP_API}${endpoint}`
    const token = globalCookies.get('XSRF-TOKEN')

    return fetch(url, {
        method: 'POST',
        headers: {
            'X-XSRF-TOKEN': token
        },
        credentials: 'include'
    }).then((response) => response.blob()).then((blob) => {
        // Create blob link to download
        const downloadURL = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = downloadURL
        link.setAttribute('download', fileName)

        // Append to html link element page
        document.body.appendChild(link)

        // Start download
        link.click()

        // Clean up and remove the link
        link.parentNode?.removeChild(link)
    })
}

/*   ARRAY FUNCTIONS
**********************************************************************************************************/
function returnIntersection(array1: [], array2: []) {
    return array1.filter((value) => array1.includes(value)).filter((value, index, self) => self.indexOf(value) === index)
}

/*   STRING FUNCTIONS
**********************************************************************************************************/
function isTwoArrayEquals<T>(a: T, b: T) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value: any, index: number) => value === b[index])
}

/*   TIMER FUNCTIONS
**********************************************************************************************************/
function Timer(callback: () => void, delay: number = 0): object {
    let timerId: number
    let start: number
    let remaining: number = delay

    const pause = (): void => {
        window.clearTimeout(timerId)
        remaining -= Date.now() - start
    }

    const cancel = (): void => {
        window.clearTimeout(timerId)
    }

    const resume = (): void => {
        start = Date.now()
        window.clearTimeout(timerId)
        timerId = window.setTimeout(callback, remaining)
    }

    resume()

    return { cancel, pause, resume }
}

/*   DATE FUNCTIONS
**********************************************************************************************************/
function parseISO(date: string): DateTime {
    return DateTime.fromISO(date)
}

function parseSQL(date: string): DateTime {
    return DateTime.fromSQL(date)
}

function parseDate(date: string): Object {
    return DateTime.fromFormat(date, 'dd/MM/yyyy')
}

export function parseDateToReadable(dateTime: string): string {
    return DateTime.fromISO(dateTime).setLocale('en-AU').toLocaleString()
}

function aud(number: string | number | undefined = 0) {
    const numberWithoutCommas = parseFloat(number.toString().replace(/,/g, ''))

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2
    }).format(numberWithoutCommas)
}

export {
    PrivateRoute,
    uid,
    regex,
    DomainContact,
    mapToAccount,
    getInvoiceStatusColour,
    getServiceStatusColour,
    getStatesByCountryCode,
    getBillingCycle,
    getInitials,
    removePropertyByKeys,
    titleCase,
    camelCase,
    snakeCase,
    getDomainName,
    formatDescription,
    extractNumberFromString,
    copyToClipboard,
    parseISOToLocalString,
    isObjectEmpty,
    flattenObject,
    renameKeys,
    removeUndefinedNullEmptyStringFromObject,
    replaceNullToEmptyString,
    downloadFile,
    returnIntersection,
    isTwoArrayEquals,
    Timer,
    parseISO,
    parseSQL,
    parseDate,
    aud
}
